import React from 'react';
import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  MenuItem,
  MenuList,
  Popover,
} from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { useHoverMenu } from '@core/hooks/useHoverMenu';
import type { ContextMenuItem } from './ContextMenu';

export interface ActionButtonProps
  extends Omit<ButtonProps, 'variant' | 'size' | 'color'> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'subtle'
    | 'danger'
    | 'ghost'
    | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  menuItems?: ContextMenuItem[];
}

const ActionButton: React.FC<ActionButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'start',
  menuItems,
  children,
  disabled,
  sx,
  ...props
}) => {
  const { isOpen, rootRef, menuPaperRef, closeMenu, rootProps } =
    useHoverMenu();

  const getVariantProps = () => {
    switch (variant) {
      case 'primary':
        return {
          variant: 'contained' as const,
          sx: {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
            ...sx,
          },
        };
      case 'secondary':
        return {
          variant: 'contained' as const,
          sx: {
            backgroundColor: 'secondary.main',
            color: 'secondary.contrastText',
            '&:hover': {
              backgroundColor: 'secondary.dark',
            },
            ...sx,
          },
        };
      case 'outline':
        return {
          variant: 'outlined' as const,
          sx: {
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
            },
            ...sx,
          },
        };
      case 'subtle':
        return {
          variant: 'outlined' as const,
          sx: {
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
            },
            ...sx,
          },
        };
      case 'danger':
        return {
          variant: 'outlined' as const,
          sx: {
            borderColor: 'error.main',
            color: 'error.main',
            '&:hover': {
              backgroundColor: 'error.main',
              color: 'error.contrastText',
            },
            ...sx,
          },
        };
      case 'ghost':
        return {
          variant: 'text' as const,
          sx: {
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            ...sx,
          },
        };
      case 'success':
        return {
          variant: 'contained' as const,
          sx: {
            backgroundColor: 'success.main',
            color: 'success.contrastText',
            '&:hover': {
              backgroundColor: 'success.dark',
            },
            ...sx,
          },
        };
      default:
        return {
          variant: 'contained' as const,
          sx,
        };
    }
  };

  const getSizeProps = () => {
    switch (size) {
      case 'xs':
        return {
          size: 'small' as const,
          sx: {
            fontSize: (theme: Theme) => theme.typography.caption.fontSize,
            padding: '2px 12px',
            minHeight: 24,
            ...getVariantProps().sx,
          },
        };
      case 'sm':
        return {
          size: 'small' as const,
          sx: {
            fontSize: (theme: Theme) => theme.typography.button.fontSize,
            padding: '4px 12px',
            minHeight: 32,
            ...getVariantProps().sx,
          },
        };
      case 'md':
        return {
          size: 'medium' as const,
          sx: {
            fontSize: (theme: Theme) => theme.typography.button.fontSize,
            padding: '8px 16px',
            minHeight: 40,
            ...getVariantProps().sx,
          },
        };
      case 'lg':
        return {
          size: 'large' as const,
          sx: {
            fontSize: (theme: Theme) => theme.typography.body1.fontSize,
            padding: '12px 24px',
            minHeight: 48,
            ...getVariantProps().sx,
          },
        };
      default:
        return {
          size: 'medium' as const,
          sx: getVariantProps().sx,
        };
    }
  };

  const sizeProps = getSizeProps();
  const variantProps = getVariantProps();

  const renderContent = () => {
    if (loading) {
      return <CircularProgress size={16} color="inherit" />;
    }

    if (icon && children) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {iconPosition === 'start' && icon}
          {children}
          {iconPosition === 'end' && icon}
        </Box>
      );
    }

    if (icon && !children) {
      return icon;
    }

    return children;
  };

  const button = (
    <Button
      {...props}
      {...variantProps}
      {...sizeProps}
      disabled={disabled || loading}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 2,
        boxShadow: 'none',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&:disabled': {
          transform: 'none',
          boxShadow: 'none',
        },
        ...sizeProps.sx,
      }}
    >
      {renderContent()}
    </Button>
  );

  return menuItems?.length ? (
    <Box {...rootProps} sx={{ display: 'inline-flex' }}>
      {button}
      <Popover
        open={isOpen}
        anchorEl={rootRef.current}
        onClose={closeMenu}
        disablePortal
        disableScrollLock
        disableAutoFocus
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            ref: menuPaperRef,
            sx: {
              minWidth: rootRef.current?.offsetWidth,
              mt: 0.25,
              pointerEvents: 'auto',
            },
          },
        }}
      >
        <MenuList autoFocusItem={false}>
          {menuItems.map((item, index) => (
            <MenuItem
              key={`${item.label}-${index}`}
              onClick={() => {
                item.onClick();
                closeMenu();
              }}
              sx={item.destructive ? { color: 'error.main' } : undefined}
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </Box>
  ) : (
    button
  );
};

export default ActionButton;
